/**
 * Centralized, typed copy catalog for the PCF Validation UI.
 *
 * English-only per AGENTS.md. Functions are used where dynamic interpolation is
 * required so the catalog can later be swapped for a real i18n implementation
 * without changing component call sites.
 */

export interface ValidationCopy {
  page: {
    title: string;
  };
  header: {
    title: string;
    runContext: {
      currentModel: string;
      lastRun: (label: string) => string;
      fullModelScope: string;
      checksEnabled: (selected: number, total: number) => string;
      embeddedMode: string;
      scope: (name: string, count: number) => string;
      totalIssues: (total: number, families: number) => string;
    };
    scopeAction: string;
    actionLabels: {
      run: string;
      running: string;
      configure: string;
      conditionStudio: string;
      guide: string;
      back: string;
      openRail: string;
      hideDetails: string;
      showDetails: string;
    };
    capabilityNote: (mode: string) => string;
  };
  rail: {
    title: string;
    description: string;
    search: {
      title: string;
      description: string;
      placeholder: string;
      ariaLabel: string;
      resetAll: string;
    };
    quickFilters: {
      title: string;
      description: string;
      allIssues: string;
      critical: string;
      errors: string;
      warnings: string;
    };
    focus: {
      title: string;
      description: (blockerCount: number) => string;
      blockersOnly: string;
      requiredFields: string;
    };
    repairArea: {
      title: string;
      description: string;
      mapping: string;
      exportData: string;
      connectors: string;
      properties: string;
      components: string;
    };
    passed: {
      title: string;
      passedChecks: string;
      reviewPassed: string;
      keepPassedAvailable: string;
      resetUnresolvedFilters: string;
    };
  };
  queue: {
    title: {
      issuesToFix: string;
      passedChecks: string;
    };
    description: {
      passedCount: (count: number) => string;
      issuesFound: (count: number) => string;
      noMatch: string;
      noUnresolvedMatch: string;
      noPassedMatch: string;
    };
    keyboardHint: string;
    keyboardAriaLabel: string;
    callout: {
      gettingStarted: {
        title: string;
        description: string;
        action: string;
      };
      noUnresolvedIssues: {
        title: string;
        description: string;
        action: string;
        passedChecksChip: (count: number) => string;
      };
    };
    ariaLabel: string;
    passedCard: {
      badge: string;
      validationLabel: (displayNumber: number, title: string) => string;
    };
  };
  card: {
    category: {
      mapping: string;
      requiredExport: string;
      connectors: string;
      properties: string;
      components: string;
    };
    impact: {
      field: (count: number) => string;
      item: (count: number) => string;
      issue: (count: number) => string;
      affectedItem: (count: number) => string;
    };
    priority: {
      blocker: string;
      required: string;
    };
    action: string;
    screenReader: {
      validationLabel: (displayNumber: number, title: string) => string;
    };
    validationNumber: (displayNumber: number) => string;
  };
  detailPanel: {
    emptyState: {
      passedReview: {
        title: string;
        description: string;
        steps: string[];
        action: string;
      };
      clear: {
        title: string;
        description: string;
        steps: string[];
      };
      noMatch: {
        title: string;
        description: string;
        steps: string[];
        action: string;
      };
      selectIssueFamily: {
        title: string;
        description: string;
        steps: string[];
      };
    };
    title: (displayNumber: number | null | undefined, title: string) => string;
    blockerBadge: string;
    recommendedAction: {
      label: string;
      fallback: string;
      runFocused: string;
      openQuickFillSlotSwap: string;
    };
    quickFix: {
      title: string;
      oneClickFixAvailable: string;
      openRepairWorkspace: string;
      undoLastQuickFix: string;
      guidedFixPrompt: string;
      reviewFixPrompt: string;
      reconnectAll: string;
      reconnectAllDescription: string;
      reconnectUnavailable: string;
      undoSuccess: string;
      undoFailed: string;
    };
    issueDetails: {
      title: string;
      type: string;
      priority: string;
      impact: string;
      issues: string;
      issuesValue: (count: number) => string;
      unknown: string;
    };
    whyThisPath: {
      title: string;
      scopeReview: string;
      directRepair: string;
    };
    fallbackGuidance: {
      whenToSwitch: string;
      fallback: string;
      hide: string;
      show: string;
    };
    alternatePaths: {
      title: string;
      open: string;
    };
    impactSummary: {
      title: string;
      affectedItems: string;
      types: string;
      instances: string;
      elementIds: string;
      noneListed: string;
      more: (count: number) => string;
    };
    moreDetails: {
      title: string;
    };
    affectedItems: {
      connectorsTitle: string;
      defaultTitle: string;
      showFewer: string;
      viewAll: (count: number) => string;
    };
    topAffectedTypes: {
      title: string;
      typeSummary: (count: number) => string;
      showFewer: string;
      viewAll: (count: number) => string;
      typeLine: (componentType: string, instanceCount: number, issueCount: number) => string;
    };
    sampleInstances: {
      sampleTitle: string;
      examplesTitle: string;
      sampleSummary: (count: number) => string;
      examplesSummary: (count: number) => string;
      showFewer: string;
      viewAll: (count: number) => string;
      instanceLine: (label: string, reason: string, ids: string[]) => string;
      exampleLine: (label: string, detail: string, ids: string[]) => string;
    };
    rawIssueDetails: {
      title: string;
      disclosureTitle: string;
      summary: (count: number) => string;
      viewRaw: string;
      rawIssues: string;
      hide: string;
      showFewer: string;
      viewAll: (count: number) => string;
    };
    copyAction: string;
    clipboard: {
      severity: string;
      category: string;
      target: string;
      affectedItems: string;
      examples: string;
      exampleLine: (label: string, detail: string) => string;
      noneListed: string;
    };
    pathLabelFallback: string;
    confidence: (label: string, reason: string) => string;
    undoProgress: {
      title: string;
      message: string;
      restoringMessage: string;
      complete: string;
      error: string;
    };
  };
  dialogs: {
    configuration: {
      title: string;
      subtitle: string;
      selectAll: string;
      deselectAll: string;
      guide: string;
      close: string;
      validationLabel: (id: number, title: string) => string;
      toggleAriaLabel: (id: number, title: string) => string;
      tourLabel: string;
    };
    quickFix: {
      dialogTitle: (label: string) => string;
      reviewRequired: string;
      plannedChange: string;
      parameter: string;
      currentValue: string;
      proposedValue: string;
      affectedElements: string;
      targets: string;
      noTargets: string;
      moreTargets: (count: number) => string;
      undoAvailable: string;
      undoLimited: string;
      currentValueAlert: string;
      selectValue: string;
      applyingTo: (count: number) => string;
      cancel: string;
      confirm: string;
      progressTitle: (label: string) => string;
      progressMessage: string;
      progressRunning: string;
      progressComplete: string;
      progressError: string;
      fixAll: {
        button: (count: number) => string;
        bulkAlert: (elementCount: number, issueCount: number) => string;
        undoAvailable: string;
        undoLimited: string;
        confirm: string;
      };
      help: {
        prerequisiteAuto: string;
        prerequisiteManual: string;
        downstream: (parameterName: string, affectedElementCount: number) => string;
        noteAuto: string;
        noteManual: string;
        fixAllPrerequisite: string;
        fixAllDownstream: (issueCount: number, affectedElementCount: number) => string;
        fixAllNote: string;
      };
    };
    reconnection: {
      titleProgressSingle: string;
      titleProgressAll: string;
      titleResults: string;
      helpLabel: string;
      gapAllowanceTitle: string;
      currentReconnectLimit: (value: number) => string;
      operationTimeline: string;
      status: string;
      started: string;
      reconnectFinished: string;
      validationRefresh: string;
      validationRefreshed: string;
      progressCheck: (attempt: number) => string;
      reconnectAttemptSummary: string;
      attemptedPairs: (count: number) => string;
      totalPairs: (count: number) => string;
      successful: (count: number) => string;
      failed: (count: number) => string;
      validationRerunning: string;
      validationGateSummary: string;
      gate: string;
      remainingConnectorBreaks: (count: number) => string;
      remainingSystemBreaks: (count: number) => string;
      remainingGeometryOverlaps: (count: number) => string;
      failureKind: string;
      touchedSystems: string;
      successfulReconnections: string;
      failedReconnections: string;
      close: string;
      progress: {
        title: (label: string) => string;
        message: string;
        queuedMessage: string;
        refreshMessage: string;
        detail: string;
        metrics: {
          totalPairs: string;
          attempted: string;
          successful: string;
          failed: string;
        };
      };
      humanizeValidationGate: (value?: string) => string;
      humanizeFailureKind: (value?: string) => string | null;
      phases: {
        running: string;
        completed: string;
      };
    };
  };
  snackbars: {
    info: {
      openedExistingConditionSet: string;
      createdNewConditionSet: string;
      clipboardUnavailable: string;
    };
    success: {
      issueCopied: string;
      quickFixUndone: string;
      quickFixCompleted: (fixed: number, total: number) => string;
      quickFixCompletedWithUndo: (fixed: number, total: number) => string;
      fixAllCompleted: (fixed: number, total: number) => string;
    };
    error: {
      quickFixFailed: (fixed: number, total: number, failed: number, reason?: string) => string;
      fixAllFailed: (fixed: number, total: number, failed: number, reason?: string) => string;
      reconnectUnavailable: string;
      undoQuickFixFailed: (message?: string) => string;
      generic: (message: string) => string;
    };
  };
  emptyState: {
    notConnected: {
      title: string;
      description: string;
      caption: string;
      stepsTitle: string;
      steps: string[];
    };
    noDocument: {
      title: string;
      description: string;
      stepsTitle: string;
      steps: string[];
    };
    connecting: {
      title: string;
      message: string;
      detail: string;
      phase: string;
      steps: string[];
    };
    loading: {
      title: string;
      message: string;
      detail: string;
      phase: string;
      steps: string[];
    };
    ready: {
      title: string;
      description: string;
      run: string;
    };
    stale: {
      title: string;
      description: string;
      run: string;
    };
  };
  navigationNotice: {
    chipLabel: string;
    helpLabel: string;
    affected: (count: number) => string;
    back: string;
    dismiss: string;
  };
  pageShell: {
    resizeRail: string;
    resizeDetail: string;
  };
  tour: {
    dialogLabel: string;
  };
}

export const validationCopy: ValidationCopy = {
  page: {
    title: 'PCF validation',
  },
  header: {
    title: 'Validation repair console',
    runContext: {
      currentModel: 'Current model',
      lastRun: (label) => `Last run ${label}`,
      fullModelScope: 'Full model scope',
      checksEnabled: (selected, total) => `${selected}/${total} checks enabled`,
      embeddedMode: 'Embedded mode',
      scope: (name, count) =>
        `Scope: ${name}${count > 0 ? ` · ${count} component${count === 1 ? '' : 's'}` : ''}`,
      totalIssues: (total, families) => `${total} total issues · ${families} families`,
    },
    scopeAction: 'Clear scope',
    actionLabels: {
      run: 'Run validation',
      running: 'Running…',
      configure: 'Configure',
      conditionStudio: 'Condition Studio',
      guide: 'Page guide',
      back: 'Back',
      openRail: 'Open triage rail',
      hideDetails: 'Hide details',
      showDetails: 'Show details',
    },
    capabilityNote: (mode) =>
      `Some repair actions stay visible for context, but ${mode} mode cannot run every fix directly.`,
  },
  rail: {
    title: 'Triage filters',
    description: 'Keep the issue queue focused.',
    search: {
      title: 'Search',
      description: 'Find issues by keyword',
      placeholder: 'Search issues…',
      ariaLabel: 'Search issues…',
      resetAll: 'Reset all',
    },
    quickFilters: {
      title: 'Quick filters',
      description: 'Severity',
      allIssues: 'All issues',
      critical: 'Critical',
      errors: 'Errors',
      warnings: 'Warnings',
    },
    focus: {
      title: 'Focus',
      description: (blockerCount) =>
        blockerCount > 0 ? `${blockerCount} blocking issues` : 'Narrow your view',
      blockersOnly: 'Blockers only',
      requiredFields: 'Required fields',
    },
    repairArea: {
      title: 'Repair category',
      description: 'Filter by category',
      mapping: 'Mapping',
      exportData: 'Export data',
      connectors: 'Connectors',
      properties: 'Properties',
      components: 'Components',
    },
    passed: {
      title: 'Passed',
      passedChecks: 'Passed checks',
      reviewPassed: 'Review passed',
      keepPassedAvailable:
        'Keep passed checks available without cluttering the unresolved issue queue.',
      resetUnresolvedFilters: 'Reset filters',
    },
  },
  queue: {
    title: {
      issuesToFix: 'Issues to fix',
      passedChecks: 'Passed checks',
    },
    description: {
      passedCount: (count) => `${count} checks passed successfully`,
      issuesFound: (count) =>
        `${count} issue families found, sorted by priority`,
      noMatch: 'No issues match your current filters',
      noUnresolvedMatch: 'No unresolved issue families match the current filters.',
      noPassedMatch: 'No passed checks match the current search.',
    },
    keyboardHint: 'Use ↑↓ to navigate',
    keyboardAriaLabel: 'Repair queue interaction tips',
    callout: {
      gettingStarted: {
        title: 'Getting started',
        description:
          'Select an issue from the list below to see details and recommended fixes. Start with Critical or Error issues first.',
        action: 'Got it',
      },
      noUnresolvedIssues: {
        title: 'No unresolved validation issues',
        description:
          'This validation run is clear. Passed checks remain available for a final review before export.',
        action: 'Review passed checks',
        passedChecksChip: (count) => `${count} passed checks`,
      },
    },
    ariaLabel: 'Validation repair queue',
    passedCard: {
      badge: 'Passed',
      validationLabel: (displayNumber, title) =>
        `Validation #${displayNumber} · ${title}`,
    },
  },
  card: {
    category: {
      mapping: 'Mapping',
      requiredExport: 'Required export data',
      connectors: 'Connectors',
      properties: 'Properties',
      components: 'Objects and components',
    },
    impact: {
      field: (count) => `${count} field${count === 1 ? '' : 's'}`,
      item: (count) => `${count} item${count === 1 ? '' : 's'}`,
      issue: (count) => `${count} issue${count === 1 ? '' : 's'}`,
      affectedItem: (count) => `${count} affected item${count === 1 ? '' : 's'}`,
    },
    priority: {
      blocker: 'BLOCKER',
      required: 'REQUIRED',
    },
    action: 'Open',
    screenReader: {
      validationLabel: (displayNumber, title) =>
        `Validation #${displayNumber} · ${title}`,
    },
    validationNumber: (displayNumber) => `Validation #${displayNumber}`,
  },
  detailPanel: {
    emptyState: {
      passedReview: {
        title: 'Passed checks review',
        description:
          'You are browsing checks that already passed. Return to unresolved families when you want to work the repair queue again.',
        steps: [
          'In the triage rail, turn off Passed checks (or use the action below) to show unresolved families.',
          'Pick one family in the repair queue to open impact and the recommended next step in detail.',
        ],
        action: 'Exit passed review',
      },
      clear: {
        title: 'Validation run is clear',
        description: 'There are no unresolved issue families in this run.',
        steps: [
          'Run validation again after mapping, Quick Fill, or model edits to refresh the queue.',
          'Optionally open Passed checks in the rail for a final review before export.',
        ],
      },
      noMatch: {
        title: 'No families match your filters',
        description:
          'Unresolved issues still exist, but nothing in the queue matches the current triage filters or search.',
        steps: [
          'Widen severity, repair area, or blocker filters — or clear search text.',
          'Use Reset filters in the triage rail to bring the full unresolved queue back.',
        ],
        action: 'Clear filters',
      },
      selectIssueFamily: {
        title: 'Select an issue family',
        description:
          'Choose one row in the repair queue to see its impact, available paths, and recommended next action.',
        steps: [
          'Triage rail: narrow scope so the next family to fix is obvious.',
          'Repair queue: select a family (see keyboard tips on the help icon in the queue header).',
          'Detail: follow Recommended next step before opening a deeper repair workspace.',
        ],
      },
    },
    title: (displayNumber, title) =>
      displayNumber != null ? `Validation #${displayNumber} · ${title}` : title,
    blockerBadge: 'BLOCKER',
    recommendedAction: {
      label: 'Recommended action',
      fallback: 'Review issue details',
      runFocused: 'Run focused validation',
      openQuickFillSlotSwap: 'Open Quick Fill for safe slot swaps',
    },
    quickFix: {
      title: 'Quick fix options',
      oneClickFixAvailable: 'One-click fix available',
      openRepairWorkspace: 'Open repair workspace',
      undoLastQuickFix: 'Undo last quick fix',
      guidedFixPrompt: 'Open the repair workspace for guided fixes on this issue.',
      reviewFixPrompt: 'Use the repair workspace to review and fix this issue.',
      reconnectAll: 'Reconnect all',
      reconnectAllDescription:
        'Attempt to reconnect every eligible connector pair in this issue family, then run validation again.',
      reconnectUnavailable: 'Reconnect all is not available for this issue.',
      undoSuccess: 'Quick fix was undone.',
      undoFailed: 'Failed to undo quick fix',
    },
    issueDetails: {
      title: 'Issue details',
      type: 'Type',
      priority: 'Priority',
      impact: 'Impact',
      issues: 'Issues',
      issuesValue: (count) => `${count} raw`,
      unknown: '-',
    },
    whyThisPath: {
      title: 'Why this path',
      scopeReview: 'Scope review recommended',
      directRepair: 'Direct repair recommended',
    },
    fallbackGuidance: {
      whenToSwitch: 'When to switch paths',
      fallback: 'Fallback guidance',
      hide: 'Hide switch guidance',
      show: 'Show switch guidance',
    },
    alternatePaths: {
      title: 'Other options',
      open: 'Open',
    },
    impactSummary: {
      title: 'Impact summary',
      affectedItems: 'Affected items',
      types: 'Types',
      instances: 'Instances',
      elementIds: 'Element IDs',
      noneListed: 'None listed',
      more: (count) => `+${count} more`,
    },
    moreDetails: {
      title: 'More details',
    },
    affectedItems: {
      connectorsTitle: 'Impacted items',
      defaultTitle: 'Affected fields and items',
      showFewer: 'Show fewer items',
      viewAll: (count) => `View all ${count} affected items`,
    },
    topAffectedTypes: {
      title: 'Top affected types',
      typeSummary: (count) =>
        `${count} type${count === 1 ? '' : 's'} in this issue family`,
      showFewer: 'Show fewer types',
      viewAll: (count) => `View all ${count} types`,
      typeLine: (componentType, instanceCount, issueCount) =>
        `${componentType} · ${instanceCount} instance${instanceCount === 1 ? '' : 's'} · ${issueCount} issue${issueCount === 1 ? '' : 's'}`,
    },
    sampleInstances: {
      sampleTitle: 'Sample impacted instances',
      examplesTitle: 'Example failures',
      sampleSummary: (count) =>
        `${count} sampled instance${count === 1 ? '' : 's'}`,
      examplesSummary: (count) => `${count} example${count === 1 ? '' : 's'}`,
      showFewer: 'Show fewer instances',
      viewAll: (count) => `View all ${count} failing instances`,
      instanceLine: (label, reason, ids) =>
        `${label} · ${reason}${ids.length > 0 ? ` · IDs: ${ids.join(', ')}` : ''}`,
      exampleLine: (label, detail, ids) =>
        `${label} · ${detail}${ids.length > 0 ? ` · IDs: ${ids.join(', ')}` : ''}`,
    },
    rawIssueDetails: {
      title: 'Raw issue details',
      disclosureTitle: 'Raw issue details',
      summary: (count) => `${count} raw issue${count === 1 ? '' : 's'} available`,
      viewRaw: 'View raw issue details',
      rawIssues: 'Raw issues',
      hide: 'Hide',
      showFewer: 'Show fewer raw issues',
      viewAll: (count) => `View all ${count} raw issues`,
    },
    copyAction: 'Copy issue list',
    clipboard: {
      severity: 'Severity',
      category: 'Category',
      target: 'Target',
      affectedItems: 'Affected items',
      examples: 'Examples',
      exampleLine: (label, detail) => `- ${label}: ${detail}`,
      noneListed: 'None listed',
    },
    pathLabelFallback: 'Review',
    confidence: (label, reason) => `${label} confidence · ${reason}`,
    undoProgress: {
      title: 'Undo quick fix',
      message: 'Quick fix undo queued for Revit.',
      restoringMessage: 'Restoring previous validation values in Revit.',
      complete: 'Quick fix undo completed.',
      error: 'Quick fix undo failed.',
    },
  },
  dialogs: {
    configuration: {
      title: 'Configure validation checks',
      subtitle: 'Select which validation checks to run',
      selectAll: 'Select all',
      deselectAll: 'Deselect all',
      guide: 'Guide',
      close: 'Close',
      validationLabel: (id, title) => `#${id}: ${title}`,
      toggleAriaLabel: (id, title) => `Toggle validation check #${id}: ${title}`,
      tourLabel: 'Validation configuration tour',
    },
    quickFix: {
      dialogTitle: (label) => label,
      reviewRequired: 'Quick fix review is required before anything is applied.',
      plannedChange: 'Planned change',
      parameter: 'Parameter',
      currentValue: 'Current value',
      proposedValue: 'Proposed value',
      affectedElements: 'Affected elements',
      targets: 'Targets',
      noTargets: 'No targets',
      moreTargets: (count) => ` +${count} more`,
      undoAvailable: 'Undo will be available after apply.',
      undoLimited:
        'Undo is limited for this quick fix because the current runtime does not expose the full previous value set.',
      currentValueAlert: 'Current value: {value}',
      selectValue: 'Select value',
      applyingTo: (count) =>
        `Applying to ${count} element${count !== 1 ? 's' : ''}`,
      cancel: 'Cancel',
      confirm: 'Confirm quick fix',
      progressTitle: (label) => label,
      progressMessage: 'Quick fix queued for Revit.',
      progressRunning: 'Running validation quick fix in Revit.',
      progressComplete: 'Validation quick fix completed.',
      progressError: 'Validation quick fix failed.',
      fixAll: {
        button: (count) => `Fix all (${count})`,
        bulkAlert: (elementCount, issueCount) =>
          `This bulk quick fix will update ${elementCount} elements across ${issueCount} issues.`,
        undoAvailable: 'Undo will be available after apply.',
        undoLimited: 'Undo is limited for this bulk quick fix.',
        confirm: 'Confirm quick fix',
      },
      help: {
        prerequisiteAuto:
          'Open the confirmation dialog and review the detected repair before applying it.',
        prerequisiteManual:
          'Open the dialog, choose or enter the replacement value, review it with the team if necessary, then confirm the planned change.',
        downstream: (parameterName, affectedElementCount) =>
          `The repair targets ${parameterName} on ${affectedElementCount} element${affectedElementCount === 1 ? '' : 's'} affected by the current validation issue.`,
        noteAuto:
          'Auto-detected fixes still stage a plan first so the repair can be reviewed.',
        noteManual:
          'Manual fixes are best for values that need an engineer-approved choice.',
        fixAllPrerequisite:
          'Use Fix all only when the matching issues should receive the same validation repair.',
        fixAllDownstream: (issueCount, affectedElementCount) =>
          `Stages one bulk repair plan across ${issueCount} matching issue${issueCount === 1 ? '' : 's'} and ${affectedElementCount} affected element${affectedElementCount === 1 ? '' : 's'}.`,
        fixAllNote:
          'The confirmation dialog shows the shared proposed value before anything is applied.',
      },
    },
    reconnection: {
      titleProgressSingle: 'Reconnect pair progress',
      titleProgressAll: 'Reconnect all progress',
      titleResults: 'Reconnection results',
      helpLabel: 'Reconnection results',
      gapAllowanceTitle: 'Gap allowance for next validation',
      currentReconnectLimit: (value) =>
        `Current reconnect limit: ${value.toFixed(1)} mm`,
      operationTimeline: 'Operation timeline',
      status: 'Status',
      started: 'Started',
      reconnectFinished: 'Reconnect finished',
      validationRefresh: 'Validation refresh',
      validationRefreshed: 'Validation refreshed',
      progressCheck: (attempt) => `Progress check: attempt ${attempt}`,
      reconnectAttemptSummary: 'Reconnect attempt summary',
      attemptedPairs: (count) => `Attempted pairs: ${count}`,
      totalPairs: (count) => `Total pairs: ${count}`,
      successful: (count) => `Successful: ${count}`,
      failed: (count) => `Failed: ${count}`,
      validationRerunning:
        'Validation is being rerun now. The visible issue list may still show the pre-reconnect report until refresh completes.',
      validationGateSummary: 'Validation gate summary',
      gate: 'Gate',
      remainingConnectorBreaks: (count) =>
        `Remaining connector breaks: ${count}`,
      remainingSystemBreaks: (count) =>
        `Remaining system breaks: ${count}`,
      remainingGeometryOverlaps: (count) =>
        `Remaining geometric overlaps: ${count}`,
      failureKind: 'Failure kind',
      touchedSystems: 'Affected systems',
      successfulReconnections: 'Successful reconnections',
      failedReconnections: 'Failed reconnections',
      close: 'Close',
      progress: {
        title: (label) => label || 'Reconnect all',
        message: 'Reconnect write',
        queuedMessage: 'Queued',
        refreshMessage: 'Validation refresh',
        detail:
          'Revit is reconnecting selected connector pairs and refreshing affected validation checks.',
        metrics: {
          totalPairs: 'Total pairs',
          attempted: 'Attempted',
          successful: 'Successful',
          failed: 'Failed',
        },
      },
      humanizeValidationGate: (value) => {
        switch (value) {
          case 'validation_1_and_22_scoped':
            return 'Validation #1 + #22 (affected scope)';
          case 'validation_1_22_28_scoped':
            return 'Validation #1 + #22 + #28 (affected scope)';
          default:
            return value || 'Validation-gated reconnect';
        }
      },
      humanizeFailureKind: (value) => {
        switch (value) {
          case 'none':
            return null;
          case 'connector_breaks_remain':
            return 'Connector breaks remain';
          case 'system_still_split':
            return 'Affected systems are still split';
          case 'connector_breaks_and_system_still_split':
            return 'Connector breaks remain and affected systems are still split';
          case 'geometry_overlap_remains':
            return 'Geometric overlaps remain';
          case 'geometry_overlap_and_connectivity_remain':
            return 'Geometric overlaps and connectivity issues remain';
          case 'validation_rerun_failed':
            return 'Validation rerun failed';
          case 'reconnect_operation_failed':
            return 'Reconnect operation failed';
          case 'no_reconnectable_issues':
            return 'No reconnectable issues found';
          default:
            return value || null;
        }
      },
      phases: {
        running: 'Running',
        completed: 'Completed',
      },
    },
  },
  snackbars: {
    info: {
      openedExistingConditionSet: 'Opened existing condition set for editing.',
      createdNewConditionSet:
        'No matching condition set found; created a new one.',
      clipboardUnavailable:
        'Clipboard unavailable. Issue group was written to the console log.',
    },
    success: {
      issueCopied: 'Issue group copied to clipboard',
      quickFixUndone: 'Quick fix was undone.',
      quickFixCompleted: (fixed, total) =>
        `Fixed ${fixed} of ${total} elements`,
      quickFixCompletedWithUndo: (fixed, total) =>
        `Fixed ${fixed} of ${total} elements. Undo is available.`,
      fixAllCompleted: (fixed, total) =>
        `Fixed ${fixed} of ${total} elements`,
    },
    error: {
      quickFixFailed: (fixed, total, failed, reason?: string) =>
        `Fixed ${fixed} of ${total} elements (${failed} failed)${reason ? ` — ${reason}` : ''}`,
      fixAllFailed: (fixed, total, failed, reason?: string) =>
        `Fixed ${fixed} of ${total} elements (${failed} failed)${reason ? ` — ${reason}` : ''}`,
      reconnectUnavailable: 'Reconnect all is not available for this issue.',
      undoQuickFixFailed: (message) =>
        message || 'Failed to undo quick fix',
      generic: (message) => message,
    },
  },
  emptyState: {
    notConnected: {
      title: 'PCF validation unavailable',
      description:
        'PCF validation requires an active Revit connection. Open PipeEX from within Revit.',
      caption: 'Open the PipeEX add-in from Revit to use this feature.',
      stepsTitle: 'How to connect',
      steps: [
        'Open your project in Revit.',
        'Launch the PipeEX add-in from the Revit ribbon.',
        'Return here — the workspace loads automatically once connected.',
      ],
    },
    noDocument: {
      title: 'No open documents',
      description:
        'Open a Revit project to run PCF validation. The active document will be displayed here once a project is loaded.',
      stepsTitle: 'What to do next',
      steps: [
        'Open or activate a project in Revit.',
        'Keep the PipeEX panel open while the model loads.',
        'The active model appears here and validation can run.',
      ],
    },
    connecting: {
      title: 'Connecting to Revit',
      message: 'Opening the Revit connection',
      detail:
        'Waiting for the Revit add-in to confirm the active model. Keep this tab open while the workspace loads.',
      phase: 'Connecting to Revit',
      steps: ['Connect to the Revit add-in', 'Fetch validation results', 'Prepare the workspace'],
    },
    loading: {
      title: 'Running PCF validation',
      message: 'Preparing validation run',
      detail:
        'The results table will update as soon as Revit starts reporting progress.',
      phase: 'Preparing',
      steps: ['Connect to the Revit add-in', 'Fetch validation results', 'Prepare the workspace'],
    },
    ready: {
      title: 'Ready to validate',
      description:
        'Run validation to check your PCF export configuration for mapping problems, missing data, and connector issues.',
      run: 'Run validation',
    },
    stale: {
      title: 'Validation report is out of date',
      description:
        'The saved validation report was generated for a different setup or model state, so it is hidden until validation is run again using the current setup.',
      run: 'Run validation again',
    },
  },
  navigationNotice: {
    chipLabel: 'Fix focus from validation',
    helpLabel: 'Fix focus from validation',
    affected: (count) => `${count} affected`,
    back: 'Back to validation',
    dismiss: 'Dismiss',
  },
  pageShell: {
    resizeRail: 'Resize triage rail',
    resizeDetail: 'Resize detail panel',
  },
  tour: {
    dialogLabel: 'Validation workspace tour',
  },
};
